'use client'

import { Switch } from '@headlessui/react'
import { cn } from '@mntn-dev/ui-utilities'
import {
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react'

import { themeBackgroundMap } from '@mntn-dev/ui-theme'

import { type TestIds, getTestProps } from '../../utils'
import { Icon } from '../icon'

type ToggleProps = Readonly<
  PropsWithChildren<
    {
      checked: boolean
      onChange: (value: boolean) => void
      disabled?: boolean
      alwaysOn?: boolean
      checkedIcon?: ReactNode
      unCheckedIcon?: ReactNode
    } & TestIds
  >
>

const Toggle = ({
  checked,
  onChange,
  disabled = false,
  alwaysOn = false,
  checkedIcon,
  unCheckedIcon,
  children,
  dataTestId,
  dataTrackingId,
}: ToggleProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(checked)
  const handleChange = (value: boolean) => {
    setIsChecked(value)
    onChange(value)
  }
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => e.stopPropagation() // Click event shouldn't bubble up. E.g., A Toggle on a table row shouldn't also trigger click event on the row.

  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={isChecked}
        disabled={disabled}
        onChange={handleChange}
        onClick={handleClick}
        className={cn(
          `group inline-flex h-8 w-14 items-center rounded-full transition ${themeBackgroundMap['secondary-inverse']}`
        )}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        <span
          className={cn(
            'group-data-[checked]:translate-x-6.5 flex h-7 w-7 shrink-0 translate-x-0.5 items-center justify-center rounded-full transition',
            {
              [`${themeBackgroundMap.info} shadow-glow-blue-lg`]:
                isChecked || alwaysOn,
              [`${themeBackgroundMap.tertiary}`]: !(isChecked || alwaysOn),
              'cursor-not-allowed opacity-50': disabled,
            }
          )}
        >
          {isChecked &&
            (checkedIcon || (
              <Icon
                name="CheckIcon"
                size="xs"
                fill="outline"
                color="primary-inverse"
              />
            ))}

          {!isChecked &&
            (unCheckedIcon || (
              <Icon
                name="XMarkIcon"
                size="xs"
                fill="outline"
                color="primary-inverse"
              />
            ))}
        </span>
      </Switch>
      {children}
    </div>
  )
}

export { Toggle, type ToggleProps }
